import React, { useState, useEffect } from 'react';
import { FormGroup, Label, Input, Button } from 'reactstrap';
import styled from 'styled-components';

const FormBox = styled.div`
  border: 2px solid #6f42c1;
  border-radius: 8px;
  padding: 20px;
  background: linear-gradient(135deg, #f3e5f5, #ffffff);
  margin-bottom: 20px;

  h5 {
    color: #6f42c1;
    margin-bottom: 15px;
    font-weight: 600;
  }
`;

const HelpText = styled.div`
  background: #e3f2fd;
  border-left: 4px solid #2196f3;
  padding: 10px;
  margin-bottom: 15px;
  border-radius: 4px;
  font-size: 13px;
  color: #1565c0;
`;

const AnswersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const AnswerItem = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const AnswerBadge = styled.span`
  background: #6f42c1;
  color: white;
  padding: 4px 10px;
  border-radius: 4px;
  font-weight: 600;
  min-width: 30px;
  text-align: center;
  font-size: 12px;
`;

const DeleteButton = styled(Button)`
  padding: 4px 10px !important;
  font-size: 12px !important;
`;

const AddButton = styled(Button)`
  margin-top: 10px;
  font-size: 13px;
`;

interface RewriteSentenceFormProps {
  formData: any;
  onChange: (data: any) => void;
}

const RewriteSentenceForm: React.FC<RewriteSentenceFormProps> = ({ formData, onChange }) => {
  const parseContent = () => {
    try {
      const parsed = typeof formData.content === 'string' && formData.content
        ? JSON.parse(formData.content)
        : null;
      
      if (parsed) {
        let correctAnswers = [''];
        if (parsed.correct_answers && Array.isArray(parsed.correct_answers)) {
          correctAnswers = parsed.correct_answers;
        } else if (parsed.correct_answer) {
          correctAnswers = [parsed.correct_answer];
        }
        
        return {
          original_sentence: parsed.original_sentence || '',
          correct_answers: correctAnswers,
          instruction: parsed.instruction || '',
        };
      }
      
      return {
        original_sentence: '',
        correct_answers: [''],
        instruction: '',
      };
    } catch {
      return {
        original_sentence: '',
        correct_answers: [''],
        instruction: '',
      };
    }
  };

  const [contentData, setContentData] = useState(parseContent());

  useEffect(() => {
    onChange({
      ...formData,
      content: JSON.stringify(contentData),
    });
  }, [contentData]);

  const handleFieldChange = (field: string, value: string) => {
    setContentData({
      ...contentData,
      [field]: value,
    });
  };

  const handleAnswerChange = (index: number, value: string) => {
    const newAnswers = [...(contentData.correct_answers || [''])];
    newAnswers[index] = value;
    setContentData({
      ...contentData,
      correct_answers: newAnswers,
    });
  };

  const handleAddAnswer = () => {
    setContentData({
      ...contentData,
      correct_answers: [...(contentData.correct_answers || ['']), ''],
    });
  };

  const handleRemoveAnswer = (index: number) => {
    if ((contentData.correct_answers || ['']).length <= 1) {
      alert('Phải có ít nhất 1 đáp án đúng!');
      return;
    }
    const newAnswers = (contentData.correct_answers || ['']).filter((_: any, i: number) => i !== index);
    setContentData({
      ...contentData,
      correct_answers: newAnswers,
    });
  };

  return (
    <FormBox>
      <h5>🔄 Nội dung câu hỏi - Viết lại câu sao cho nghĩa không đổi</h5>
      
      <HelpText>
        💡 <strong>Hướng dẫn:</strong> Nhập câu gốc và câu viết lại có nghĩa tương đương.
        <br/>
        Ví dụ: "He is too young" → "He is not old enough"
      </HelpText>

      <FormGroup>
        <Label>Câu gốc: *</Label>
        <Input
          type="textarea"
          value={contentData.original_sentence || ''}
          onChange={(e) => handleFieldChange('original_sentence', e.target.value)}
          placeholder="Nhập câu gốc...
Ví dụ: He is too young to drive a car."
          rows={3}
        />
        <small className="form-text text-muted">
          Đây là câu ban đầu mà học sinh cần viết lại
        </small>
      </FormGroup>

      <FormGroup>
        <Label>Cụm từ gợi ý: (Tùy chọn)</Label>
        <Input
          type="textarea"
          value={contentData.instruction || ''}
          onChange={(e) => handleFieldChange('instruction', e.target.value)}
          placeholder="Nhập gợi ý cho học sinh...
Ví dụ: Sử dụng cấu trúc 'too...to' hoặc 'not...enough'"
          rows={2}
        />
        <small className="form-text text-muted">
          Hướng dẫn hoặc gợi ý giúp học sinh làm câu hỏi (hiển thị trên đề thi)
        </small>
      </FormGroup>

      <FormGroup>
        <Label>Các câu viết lại đúng: *</Label>
        <AnswersList>
          {(contentData.correct_answers || ['']).map((answer: string, index: number) => (
            <AnswerItem key={index}>
              <AnswerBadge>{index + 1}</AnswerBadge>
              <Input
                type="textarea"
                value={answer}
                onChange={(e) => handleAnswerChange(index, e.target.value)}
                placeholder={`Đáp án ${index + 1}...`}
                rows={2}
                style={{ flex: 1 }}
              />
              <DeleteButton
                color="danger"
                size="sm"
                onClick={() => handleRemoveAnswer(index)}
                disabled={(contentData.correct_answers || ['']).length <= 1}
              >
                ✕
              </DeleteButton>
            </AnswerItem>
          ))}
        </AnswersList>
        <AddButton
          color="success"
          size="sm"
          onClick={handleAddAnswer}
        >
          + Thêm đáp án
        </AddButton>
        <small className="form-text text-muted" style={{ marginTop: '10px' }}>
          💡 Có thể có nhiều cách viết lại đúng với nghĩa tương đương
        </small>
      </FormGroup>

      {/* Preview */}
      <div style={{ 
        background: '#f3e5f5', 
        padding: '12px', 
        borderRadius: '4px',
        marginTop: '15px'
      }}>
        <strong style={{ fontSize: '13px', color: '#6f42c1' }}>📋 Xem trước:</strong>
        <div style={{ marginTop: '8px', fontSize: '14px' }}>
          <div style={{ 
            padding: '8px', 
            background: 'white', 
            borderRadius: '4px',
            marginBottom: '8px'
          }}>
            <em style={{ color: '#666' }}>Câu gốc:</em>
            <br/>
            {contentData.original_sentence || <span style={{ color: '#999' }}>(chưa nhập)</span>}
          </div>
          <div style={{ 
            padding: '8px', 
            background: 'white', 
            borderRadius: '4px',
            border: '2px solid #6f42c1'
          }}>
            <em style={{ color: '#666' }}>Các câu viết lại đúng:</em>
            <ul style={{ margin: '5px 0 0 20px', paddingLeft: 0 }}>
              {(contentData.correct_answers || ['']).filter((a: string) => a.trim()).map((ans: string, idx: number) => (
                <li key={idx}><strong>{ans}</strong></li>
              ))}
              {(contentData.correct_answers || ['']).filter((a: string) => a.trim()).length === 0 && (
                <li><span style={{ color: '#999' }}>(chưa nhập)</span></li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* Debug JSON */}
      <details style={{ marginTop: '15px' }}>
        <summary style={{ cursor: 'pointer', color: '#666', fontSize: '14px' }}>
          🔍 Xem JSON (Debug)
        </summary>
        <pre style={{ 
          background: '#f5f5f5', 
          padding: '10px', 
          borderRadius: '4px',
          fontSize: '12px',
          maxHeight: '200px',
          overflow: 'auto',
          marginTop: '10px'
        }}>
          {JSON.stringify(contentData, null, 2)}
        </pre>
      </details>
    </FormBox>
  );
};

export default RewriteSentenceForm;
